import React, {useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {Flex, Box, useTheme, Image, Button, chakra} from '@chakra-ui/react';
import {TextA18, TextA20, TextG11, TextG12, TextG14, TextG8, TextG16, TextG20} from '../typography';
import useAuth from '../../hooks/useAuth';
import Logo from '../../assets/logos/logo_48x48.png';
import RequiresEarlyAccess from '../RequiresEarlyAccess';
import HomePagePromoBanner from '../HomePagePromoBanner';
import dayjs from 'dayjs';
import {BlueButton} from '../button';
import Icon from '../Icon';
import ConnectionGuaranteedIconSvg from '../../assets/icons/connection-guaranteed.svg';

const ConnectionGuaranteedIcon = chakra(ConnectionGuaranteedIconSvg);

const AMBASSADOR_PRESENT_TOPIC = process.env.NODE_ENV === 'production' ? 336 : 223;

const homeQuickLinksData = [
  // {
  //   value: 'events',
  //   link: '/search/event?walkaboutOfficial=1',
  //   label: 'Walkabout Events',
  //   color: '#E02F84',
  //   icon: Logo,
  // },
  {
    value: 'first_look_apartments',
    link: '/search/apartment',
    label: 'First Look Apartments',
    color: '#5976BC',
    icon: Logo,
  },
  {
    value: 'places',
    link: '/search/place',
    label: 'Places',
    color: '#92217B',
  },
  // {
  //   value: 'posts',
  //   link: `/search/post`,
  //   label: 'Posts',
  //   color: '#FF6813',
  // },
  // {
  //   value: 'group_chat',
  //   link: `/lifestyle/chat`,
  //   label: 'Group Chat',
  //   color: '#7D3EEE',
  // },
  // {
  //   value: 'offers',
  //   link: `/search/offer`,
  //   label: 'Offers',
  //   color: '#BD60D4',
  // },
  // {
  //   value: 'partners',
  //   link: `/search/partner`,
  //   label: 'Walkabout Partners',
  //   color: '#54937C',
  // },
  // {
  //   value: 'partners',
  //   link: `/search/partner`,
  //   label: 'Partners',
  //   color: '#FF6813',
  // },
];

const HomeQuickLinks = () => {
  const navigate = useNavigate();
  const {user} = useAuth();
  const theme = useTheme();

  return (
    <>
      <TextG20 mb={3} fontWeight="700">
        Know Your Neighborhood
      </TextG20>

      <Flex gap={3} mb="16px">
        <Box
          as="button"
          flex={1}
          h="56px"
          background="#276ACD"
          borderRadius="100px"
          border="none"
          cursor="pointer"
          onClick={() => navigate('/bookmark-places')}
          boxShadow="inset 0 -4px 0 rgba(0, 0, 0, 0.25)">
          <TextG16 fontWeight="500" color="white" textAlign="center">
            Where to Go
          </TextG16>
        </Box>
        <Box
          as="button"
          flex={1}
          h="56px"
          background="#C9136B"
          borderRadius="100px"
          border="none"
          cursor="pointer"
          onClick={() => navigate('/bookmark-ahead')}
          boxShadow="inset 0 -4px 0 rgba(0, 0, 0, 0.25)">
          <TextG16 fontWeight="500" color="white" textAlign="center">
            What to Do
          </TextG16>
        </Box>
      </Flex>

      {/* See Recent Notifications Row */}
      <Flex
        background="white"
        border="2px solid #E4E8EC"
        borderRadius="12px"
        p={3}
        mb={4}
        cursor="pointer"
        align="center"
        justify="space-between"
        onClick={() => navigate('/notifications')}>
        <Flex align="center" gap={2}>
          <Icon
            iconName="fi fi-sr-bell"
            style={{
              color: theme.colors.rose,
              fontSize: '18px',
            }}
          />
          <TextG14 fontWeight="700" color="#202020">
            See Recent Notifications
          </TextG14>
        </Flex>
        <Icon
          iconName="fi fi-rr-angle-small-right"
          style={{
            color: '#aaa',
            fontSize: '18px',
            height: '18px',
            width: '18px',
          }}
        />
      </Flex>

      <TextG20 mb={3} mt="5px" fontWeight="700">
        Explore What's Near You
      </TextG20>

      {
        <Flex
          w="100%"
          flexWrap="wrap"
          justifyContent="center"
          id="quick-links-tooltip"
          // templateRows="repeat(2, 1fr)" // 2 rows
          // templateColumns="repeat(3, 1fr)" // 3 columns
          px={0}
          mx={0}
          rowGap={3}
          columnGap={2.5}
          mb={3}
          position="relative">
          {homeQuickLinksData.map((item, i) => {
            return (
              <Flex
                flexGrow={1}
                flexBasis={1}
                flexDir="column"
                pos="relative"
                minW="102px"
                background="white"
                border="2px solid #E4E8EC"
                boxShadow="0px 1px 2px 0px rgba(121, 121, 141, 0.10), 0px 1px 4px 0px rgba(121, 121, 141, 0.10)"
                key={i}
                textAlign="center"
                align="center"
                justify="center"
                gap={0}
                height="44px"
                py={2}
                px={4}
                borderRadius="10px"
                onClick={() => {
                  navigate(item.link);
                }}>
                <Flex alignItems="center" gap="3px">
                  {/* {item.icon && <Image w="12px" h="12px" src={item.icon} />} */}
                  <TextG12 fontWeight="500" color={item.color}>
                    {item.label}
                  </TextG12>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      }

      <Flex gap="6px">
        <Button
          onClick={() => navigate('/search/activities')}
          h="50px"
          mt="0px !important"
          boxShadow="0px 1px 2px 0px rgba(121, 121, 141, 0.10), 0px 1px 4px 0px rgba(121, 121, 141, 0.10)"
          w="100%"
          background="white"
          color="#E02F84"
          border="2px solid #E4E8EC"
          borderRadius="10px">
          <TextG12 whiteSpace="normal" fontWeight="500">
            Events & Meet-Ups
          </TextG12>
        </Button>
        {/* <Button
          onClick={() => navigate('/search/meet-up')}
          h="50px"
          mt="0px !important"
          boxShadow="0px 1px 2px 0px rgba(121, 121, 141, 0.10), 0px 1px 4px 0px rgba(121, 121, 141, 0.10)"
          w="100%"
          background="white"
          color="#3FB8FC"
          border="2px solid #E4E8EC"
          borderRadius="10px">
          <TextG14 fontWeight="500">Meet-Ups</TextG14>
        </Button> */}
      </Flex>

      <HomePagePromoBanner />
    </>
  );
};

export default HomeQuickLinks;
